import { Box, List } from "@mui/material";
import { type ReactNode } from "react";

import { SelectableListItem } from "../SelectableListItem/SelectableListItem";
import { Text } from "../Text";

export interface SelectableOptionListOption<TValue extends string> {
  value: TValue;
  label: ReactNode;
}

export interface SelectableOptionListProps<TValue extends string> {
  options: Array<SelectableOptionListOption<TValue>>;
  selectedValue: TValue;
  onSelect: (value: TValue) => void;
  /** Optional caption rendered above the options, inside the same bordered card. */
  header?: ReactNode;
}

/**
 * Bordered card of radio-selectable options, used by both the daily-view
 * apply-to-scope picker and the bulk-posting time-override scope picker.
 */
export function SelectableOptionList<TValue extends string>(
  props: SelectableOptionListProps<TValue>
) {
  const { options, selectedValue, onSelect, header } = props;

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.border?.base}`,
        borderRadius: theme.borderRadius?.xLarge,
        overflow: "hidden",
      })}
    >
      {header ? (
        <Box
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.palette.border?.base}`,
            padding: theme.spacing(4),
            display: "flex",
            justifyContent: "center",
          })}
        >
          {header}
        </Box>
      ) : null}
      <List disablePadding>
        {options.map((option, index) => (
          <Box
            key={option.value}
            sx={(theme) => ({
              borderBottom:
                index < options.length - 1 ? `1px solid ${theme.palette.border?.base}` : "none",
            })}
          >
            <SelectableListItem
              isSelected={selectedValue === option.value}
              selectionMode="radio"
              onSelect={() => {
                onSelect(option.value);
              }}
            >
              <Text semibold>{option.label}</Text>
            </SelectableListItem>
          </Box>
        ))}
      </List>
    </Box>
  );
}
